import React from 'react';
import styles from './M_NotificationItem.module.css';
import cn from 'classnames';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import {
  Notification,
  InviteResponseNotification,
} from '@/types/notifications';
import { useTranslation } from '@/hooks/useTranslation';
import { useCountdownToDate } from '@/hooks/useCountdownToDate';
import A_UserAvatar from '@/components/A_Atoms/A_UserAvatar/A_UserAvatar';

interface M_NotificationItemProps {
  notification: Notification;
  onAccept?: () => void;
  onDecline?: () => void;
  onReinvite?: () => void;
  onRead?: () => void;
  onCancel?: () => void;
}

export default function M_NotificationItem({
  notification,
  onAccept,
  onDecline,
  onReinvite,
  onRead,
  onCancel,
}: M_NotificationItemProps) {
  const t = useTranslation();
  const countdownTarget =
    notification.type === 'custodian_activated'
      ? (notification.data.activatedAt ?? null)
      : null;
  const countdownParts = useCountdownToDate(countdownTarget);

  const formatCountdown = (): string => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const { days, hours, minutes, seconds } = countdownParts;
    if (days > 0) {
      const daysShort = t('profileNotifications.countdownDaysShort');
      return `${days} ${daysShort} ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  const renderNotificationText = () => {
    let messageKey = '';
    let replacements: Record<string, string> = {};

    switch (notification.type) {
      case 'invite':
        messageKey = 'profileNotifications.messages.invite';
        replacements = {
          inviterName: notification.data.inviterName || '',
          capsuleName: notification.data.capsuleName || '',
        };
        break;

      case 'invite_response': {
        const data = (notification as InviteResponseNotification).data;
        messageKey =
          data.responseType === 'declined'
            ? 'profileNotifications.messages.invite_response_declined'
            : 'profileNotifications.messages.invite_response_accepted';
        replacements = {
          actorName: data.actorName || '',
          capsuleName: data.capsuleName || '',
        };
        break;
      }

      case 'custodian_invite':
        messageKey = 'profileNotifications.messages.custodian_invite';
        replacements = {
          capsuleName: notification.data.capsuleName || '',
        };
        break;

      case 'custodian_activated':
        messageKey = 'profileNotifications.messages.custodian_activated';
        replacements = {
          capsuleName: notification.data.capsuleName || '',
        };
        break;

      case 'capsule_opened':
        messageKey = 'profileNotifications.messages.capsule_opened';
        replacements = {
          capsuleName: notification.data.capsuleName || '',
        };
        break;

      case 'coauthor_declined':
        messageKey = 'profileNotifications.messages.coauthor_declined';
        replacements = {
          declinerName: notification.data.declinerName || '',
          capsuleName: notification.data.capsuleName || '',
        };
        break;

      case 'death_report':
        messageKey = 'profileNotifications.messages.death_report';
        replacements = {
          reporterName: notification.data.reporterName || '',
          capsuleName: notification.data.capsuleName || '',
        };
        break;

      case 'donation':
        messageKey = 'profileNotifications.messages.donation';
        replacements = {
          donorName: notification.data.donorName || '',
          amount: notification.data.amount?.toString() || '',
          treeName: notification.data.treeName || '',
          capsuleName: notification.data.capsuleName || '',
        };
        break;

      case 'fundraising_started':
        messageKey = 'profileNotifications.messages.fundraising_started';
        replacements = {
          capsuleName: notification.data.capsuleName || '',
          initiatorName: notification.data.initiatorName || '',
          treeName: notification.data.treeName || '',
        };
        break;

      case 'fundraising_completed':
        messageKey = 'profileNotifications.messages.fundraising_completed';
        replacements = {
          capsuleName: notification.data.capsuleName || '',
          treeName: notification.data.treeName || '',
        };
        break;

      case 'reaction':
        messageKey = 'profileNotifications.messages.reaction';
        replacements = {
          reactorName: notification.data.reactorName || '',
          capsuleName: notification.data.capsuleName || '',
        };
        break;

      default:
        return null;
    }

    let message = t(messageKey);
    for (const [key, value] of Object.entries(replacements)) {
      message = message.replace(`{${key}}`, value);
    }

    return <p className={cn(styles.title, 'text-p-7')}>{message}</p>;
  };

  const renderCustodianActivatedTimer = () => {
    if (notification.type !== 'custodian_activated') return null;
    if (!countdownTarget) return null;
    return (
      <p className={cn(styles.timer_line, 'text-p-7')}>
        {t('profileNotifications.timeLeft')} {formatCountdown()}
      </p>
    );
  };

  const showActionButtons =
    !notification.isRead &&
    (notification.type === 'invite' ||
      notification.type === 'custodian_invite');
  const showReinviteButton = notification.type === 'coauthor_declined';
  const showCancelButton = notification.type === 'custodian_activated';
  const showDeathReportCancelButton = notification.type === 'death_report';

  return (
    <div
      className={cn(styles.wrapper, {
        [styles.readen]: notification.isRead,
        [styles.unread]: !notification.isRead,
      })}
    >
      {!notification.isRead && <span className={styles.read_indicator}></span>}
      <div className={styles.content_wrapper}>
        <div className={styles.image_wrapper}>
          {notification.image ? (
            <img src={notification.image} alt='avatar' />
          ) : (
            <A_UserAvatar width={28} height={28} />
          )}
        </div>
        <div className={styles.text_block}>
          {renderNotificationText()}
          {renderCustodianActivatedTimer()}
        </div>
      </div>
      <div className={styles.buttons_wrapper}>
        {showActionButtons && (
          <div className={styles.interactive_buttons_wrapper}>
            <A_Button
              variant='tertiary'
              size='small'
              fontSize='text-p-4'
              onClick={onAccept}
            >
              {t('profileNotifications.actions.accept')}
            </A_Button>
            <A_Button
              variant='tertiary'
              size='small'
              fontSize='text-p-4'
              onClick={onDecline}
            >
              {t('profileNotifications.actions.decline')}
            </A_Button>
          </div>
        )}
        {showReinviteButton && (
          <A_Button
            variant='tertiary'
            size='small'
            fontSize='text-p-4'
            onClick={onReinvite}
          >
            {t('profileNotifications.actions.reinvite')}
          </A_Button>
        )}
        {showCancelButton && onCancel && (
          <A_Button
            variant='tertiary'
            size='small'
            fontSize='text-p-4'
            onClick={onCancel}
          >
            {t('profileNotifications.actions.cancel')}
          </A_Button>
        )}
        {showDeathReportCancelButton && onDecline && (
          <A_Button
            variant='tertiary'
            size='small'
            fontSize='text-p-4'
            onClick={onDecline}
          >
            {t('profileNotifications.actions.cancelDeathReport')}
          </A_Button>
        )}
        {!notification.isRead && (
          <button
            className={cn(styles.read_button, 'text-p-4')}
            onClick={onRead}
          >
            {t('profileNotifications.actions.read')}
          </button>
        )}
      </div>
    </div>
  );
}
